/* eslint-disable react/forbid-prop-types */
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  OptionGroup, Option, ScrollSidebar, MainSidebar,
} from './styled/index.styled';
import getDefaultStyle, { legacyRemap } from '../utils/chartTools/getDefaultStyle';

import MainTitle from './MainTitle';
import MainDD from './MainDD';

import { Controllers as TableControllers } from '../charts/Table';
import { Controllers as ValueControllers } from '../charts/Value';
import { Controllers as BarControllers } from '../charts/Bar';
import { Controllers as LineControllers } from '../charts/Line';
import { Controllers as PieControllers } from '../charts/Pie';
import { Controllers as RadarControllers } from '../charts/Radar';
import { Controllers as MapControllers } from '../charts/Map';
import { Controllers as FunnelControllers } from '../charts/Funnel';
import { Controllers as RadialBarControllers } from '../charts/RadialBar';
import { Controllers as CalendarControllers } from '../charts/Calendar';
import { Controllers as WaffleControllers } from '../charts/Waffle';
import { Controllers as BoxPlotControllers } from '../charts/BoxPlot';
import { Controllers as ScatterPlotControllers } from '../charts/ScatterPlot';
import { Controllers as SubsetTableControllers } from '../charts/SubsetTable';
import { Controllers as HistogramControllers } from '../charts/Histogram';
import { Controllers as ParetoControllers } from '../charts/Pareto';
import { Controllers as BulletControllers } from '../charts/Bullet';
import { Controllers as GaugeControllers } from '../charts/Gauge';

const controllersGallery = {
  Bar: BarControllers,
  Pie: PieControllers,
  Line: LineControllers,
  Value: ValueControllers,
  Radar: RadarControllers,
  Map: MapControllers,
  Funnel: FunnelControllers,
  RadialBar: RadialBarControllers,
  Table: TableControllers,
  Calendar: CalendarControllers,
  Waffle: WaffleControllers,
  BoxPlot: BoxPlotControllers,
  ScatterPlot: ScatterPlotControllers,
  SubsetTable: SubsetTableControllers,
  Histogram: HistogramControllers,
  Pareto: ParetoControllers,
  Bullet: BulletControllers,
  Gauge: GaugeControllers,
};

const getMainControllersByType = (type) => [MainTitle, controllersGallery[type]];

const getDDControllersByType = (type) => [MainDD, controllersGallery[type]];

const customSideDefaultPropHandle = () => {};

const CustomSide = ({
  type = '',
  handle = customSideDefaultPropHandle,
  handleDD = customSideDefaultPropHandle,
  config = undefined,
  data = undefined,
  selectedLine = undefined,
  selectedLineMap = undefined,
  selectedColumn = undefined,
  selectedColumnMap = undefined,
  selectedValue = undefined,
  selectedFunction = undefined,
  selectedDrilldown = undefined,
  isGlobalThemeActive = false,
  isGlobalFontActive = false,
  advancedChartsAllowed = false,
}) => {
  const [extend, setExtend] = useState('title');
  const [styleCategory, setStyleCategory] = useState('MAIN');

  useEffect(() => {
    setExtend('title');
    setStyleCategory('MAIN');
  }, [type]);

  const handleStyleCategory = (category) => {
    setStyleCategory(category);
    setExtend(category === 'MAIN' ? 'title' : 'chart');
  };

  const style = useMemo(() => (
    { ...getDefaultStyle(config?.type || type), ...legacyRemap(config) }
  ), [config, type]);

  const [CommonControllers, KpiControllers] = styleCategory === 'DRILLDOWN' ? (
    getDDControllersByType(style.DrilldownStyle)
  ) : (
    getMainControllersByType(style.type)
  );

  return (data && style ? (
    <>
      {!!selectedDrilldown?.value && (
        <OptionGroup>
          <Option
            onClick={() => handleStyleCategory('MAIN')}
            active={styleCategory === 'MAIN'}
          >
            Principal
          </Option>
          <Option
            onClick={() => handleStyleCategory('DRILLDOWN')}
            active={styleCategory === 'DRILLDOWN'}
          >
            Subgráfico
          </Option>
        </OptionGroup>
      )}
      <ScrollSidebar>
        <MainSidebar>
          {CommonControllers ? (
            <CommonControllers
              handle={handle}
              config={style}
              extend={extend}
              setExtend={setExtend}
              isGlobalThemeActive={isGlobalThemeActive}
              isGlobalFontActive={isGlobalFontActive}
              advancedChartsAllowed={advancedChartsAllowed}
            />
          ) : <></>}
          {KpiControllers ? (
            <KpiControllers
              key={styleCategory}
              handle={styleCategory === 'DRILLDOWN' ? handleDD : handle}
              config={styleCategory === 'DRILLDOWN' ? (style.DDStyles ?? {}) : style}
              data={data}
              extend={extend}
              setExtend={setExtend}
              selectedValue={selectedValue}
              selectedLine={selectedLine}
              selectedLineMap={selectedLineMap}
              selectedColumn={selectedColumn}
              selectedColumnMap={selectedColumnMap}
              selectedFunction={selectedFunction}
              selectedDrilldown={selectedDrilldown}
              isGlobalThemeActive={isGlobalThemeActive}
              isGlobalFontActive={isGlobalFontActive}
              forDrilldown={styleCategory === 'DRILLDOWN'}
            />
          ) : <></>}
        </MainSidebar>
      </ScrollSidebar>
    </>
  ) : <></>);
};

CustomSide.propTypes = {
  config: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any),
  handle: PropTypes.func,
  handleDD: PropTypes.func,
  type: PropTypes.string,
  selectedColumn: PropTypes.objectOf(PropTypes.any),
  selectedColumnMap: PropTypes.objectOf(PropTypes.any),
  selectedValue: PropTypes.objectOf(PropTypes.any),
  selectedLine: PropTypes.objectOf(PropTypes.any),
  selectedLineMap: PropTypes.objectOf(PropTypes.any),
  selectedFunction: PropTypes.objectOf(PropTypes.any),
  selectedDrilldown: PropTypes.objectOf(PropTypes.any),
  isGlobalThemeActive: PropTypes.bool,
  isGlobalFontActive: PropTypes.bool,
};

export default CustomSide;
